import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
  ArrowDownIcon, ArrowNarrowLeftIcon, ArrowNarrowRightIcon, ArrowUpIcon,
} from '@heroicons/react/outline';

const cardStyles = {
  blue: 'bg-blue-regular',
  yellow: 'bg-yellow-regular',
  green: 'bg-green-regular',
  indigo: 'bg-black-400',
  red: 'bg-red-regular',
  gray: 'bg-black-300',
};

const labelStyles = {
  blue: 'text-blue-regular',
  yellow: 'text-yellow-regular',
  green: 'text-green-regular',
  indigo: 'text-black-600',
  red: 'text-red-regular',
  gray: 'text-black-500',
};

const labelText = {
  'AuthCh:': 'AUTH CH',
  'FuelReb:': 'FUEL REB',
  'FuelNet:': 'FUEL NET',
  'R & M:': 'R & M',
};

const CurrentSettlementCard = ({
  label, amount, payMile, percent, color, trend, isLoading,
}) => {
  const [isHover, setIsHover] = useState(false);
  const formatAmount = (val) => (_.isString(val) ? val.split('.')[0] : val);
  const cleanLabel = labelText[label] || String(label || '').replace(':', '').toUpperCase();
  const percentLabel = label === 'FuelReb:' ? 'of fuel' : 'of gross';
  const trendDirection = _.isString(trend) ? trend : trend?.direction;
  const trendPercent = _.isString(trend) ? null : trend?.percent;
  const showTrend = !isLoading && trendDirection;

  const trendStyles = {
    up: 'bg-green-light text-green-regular',
    down: 'bg-red-light text-red-regular',
    same: 'bg-yellow-light text-yellow-regular',
  };

  const getTrendIcon = () => {
    if (trendDirection === 'up') {
      return <ArrowUpIcon className="h-3 w-3" />;
    }
    if (trendDirection === 'down') {
      return <ArrowDownIcon className="h-3 w-3" />;
    }
    return (
      <span className="relative block h-3 w-3">
        <ArrowNarrowLeftIcon className="absolute left-0 top-0.5 h-3 w-3" />
        <ArrowNarrowRightIcon className="absolute left-0 -top-0.5 h-3 w-3" />
      </span>
    );
  };

  return (
    <div
      className={`relative min-h-32 overflow-hidden rounded-lg border border-black-100 bg-white p-4 shadow-sm transition-all
      ${isHover ? 'drop-shadow-small' : ''}`}
      onMouseEnter={() => {
        setIsHover(true);
      }}
      onMouseLeave={() => {
        setIsHover(false);
      }}
    >
      <span className={`absolute left-0 right-0 top-0 h-1 ${cardStyles[color] || cardStyles.gray}`} />
      {isLoading ? (
        <div className="h-full min-h-16 animate-pulse rounded-md bg-black-100" />
      ) : (
        <>
          <div className="mb-1 flex items-start justify-between gap-3">
            <h2 className={`pt-0.5 text-s font-bold uppercase ${labelStyles[color] || labelStyles.gray}`}>
              {cleanLabel}
            </h2>
            {showTrend && (
              <span
                className={`inline-flex min-w-max items-center gap-1 rounded-full px-2 py-0.5 text-s font-bold ${trendStyles[trendDirection] || trendStyles.same}`}
                title="Compared with previous settlement week"
              >
                {getTrendIcon()}
                {trendPercent || 'new'}
              </span>
            )}
          </div>
          <div className="flex items-end justify-between gap-2">
            {amount !== null && (
              <h3 className="min-w-0 text-2xl font-bold leading-none text-black-900">
                {label === 'MPG:' ? amount : formatAmount(amount)}
              </h3>
            )}
          </div>
          <div className="mt-4 grid min-h-10 grid-cols-2 gap-2 border-t border-black-100 pt-3 text-xs">
            {payMile !== null && (
              <div>
                <p className="text-black-400">per mile</p>
                <p className={`text-sm font-semibold ${labelStyles[color] || labelStyles.gray}`}>{payMile}</p>
              </div>
            )}
            {percent !== null && (
              <div className="text-right">
                <p className="text-black-400">{percentLabel}</p>
                <p className={`text-sm font-semibold ${labelStyles[color] || labelStyles.gray}`}>{percent}</p>
              </div>
            )}
            {payMile === null && percent === null && (
              <div>
                <p className="text-black-400">no change</p>
                <p className="text-sm font-semibold text-black-600">--</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

CurrentSettlementCard.propTypes = {
  label: PropTypes.string,
  amount: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  payMile: PropTypes.string,
  percent: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  color: PropTypes.string,
  trend: PropTypes.oneOfType([
    PropTypes.oneOf(['up', 'down', 'same']),
    PropTypes.shape({
      direction: PropTypes.oneOf(['up', 'down', 'same']),
      percent: PropTypes.string,
    }),
  ]),
  isLoading: PropTypes.bool,
};

CurrentSettlementCard.defaultProps = {
  label: '',
  amount: '',
  payMile: '',
  percent: '',
  color: '',
  trend: 'same',
  isLoading: false,
};

export default CurrentSettlementCard;
